import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Wrench, RefreshCw, AlertTriangle, CheckCircle2, StopCircle, ExternalLink,
} from "lucide-react";

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const LoadedTools = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // {row, kind: 'unmount' | 'endrun'}
  const [toast, setToast] = useState("");

  const isPrivileged = ["Main Admin", "Supervisor", "Inspector"].includes(user?.role);

  const fetchRows = () => {
    setLoading(true);
    api.get("/tool-mount/state")
      .then((r) => setRows(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const onUnmounted = (msg) => {
    setToast(msg);
    setModal(null);
    setTimeout(() => setToast(""), 3500);
    fetchRows();
  };

  const nowMinusDays = (isoStr) => {
    if (!isoStr) return null;
    try {
      const t = new Date(isoStr).getTime();
      return Math.max(0, (Date.now() - t) / 86400000);
    } catch {
      return null;
    }
  };

  const inProdCount = rows.filter((r) => r.has_open_job).length;
  const idleCount = rows.length - inProdCount;
  const stale = rows.filter((r) => !r.has_open_job && (r.days_mounted || 0) >= 7).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/home")}
            className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="loaded-tools-back"
          >
            <ArrowLeft size={14} /> Back to Home
          </button>
          <button
            data-testid="loaded-tools-refresh"
            onClick={fetchRows}
            className="text-xs font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>

        <div className="bg-gradient-to-r from-[#1A3560] to-[#2d4a7d] text-white rounded-2xl p-5 shadow-lg mb-4">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Currently Loaded Tools</p>
          <h1 className="text-2xl font-extrabold inline-flex items-center gap-2">
            <Wrench size={22} /> {rows.length} tool{rows.length !== 1 ? "s" : ""} on machines
          </h1>
          <div className="mt-2 flex flex-wrap gap-4 text-xs">
            <span><b>{inProdCount}</b> in production</span>
            <span><b>{idleCount}</b> mounted & idle</span>
            {stale > 0 && (
              <span className="text-amber-300">
                <b>{stale}</b> idle &gt; 7 days
              </span>
            )}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded-lg mb-3">{error}</div>}
        {toast && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-2 rounded-lg mb-3 inline-flex items-center gap-2">
            <CheckCircle2 size={14} /> {toast}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <Wrench className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-sm text-slate-500">No tools are currently mounted on any machine.</p>
              <p className="text-xs text-slate-400 mt-1">Mount status is recorded automatically when Stage-1 starts.</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="loaded-tools-table">
              <thead className="bg-slate-50 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left">Machine</th>
                  <th className="px-4 py-3 text-left">Tool</th>
                  <th className="px-4 py-3 text-left">Mounted at</th>
                  <th className="px-4 py-3 text-left">Duration</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Mounted by</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const days = r.days_mounted != null ? r.days_mounted : nowMinusDays(r.mounted_at);
                  const isStale = !r.has_open_job && (days || 0) >= 7;
                  return (
                    <tr key={r.machine_code} data-testid={`loaded-tool-row-${r.machine_code}`}
                      className={isStale ? "bg-red-50" : ""}>
                      <td className="px-4 py-3 font-extrabold text-slate-900">{r.machine_code}</td>
                      <td className="px-4 py-3">T{r.tool_no}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {r.mounted_at ? new Date(r.mounted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {days == null ? "—" : days >= 1 ? `${days.toFixed(1)} days` : `${(days * 24).toFixed(1)} hrs`}
                        {isStale && <span className="ml-1 text-[10px] font-extrabold text-red-700">STALE</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.has_open_job ? (
                          <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
                            In Production · {r.open_job?.job_id}
                          </span>
                        ) : (
                          <span className="text-[10px] font-extrabold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                            Mounted · Idle
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{r.mounted_by_name || r.mounted_by || "—"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`open-deep-dive-${r.machine_code}`}
                          onClick={() => navigate(`/admin/machine-deep-dive/${r.machine_code}`)}
                          className="text-[11px] font-bold text-[#1A3560] hover:bg-[#1A3560]/10 px-2 py-1 rounded inline-flex items-center gap-1 mr-2"
                        >
                          <ExternalLink size={11} /> Deep Dive
                        </button>
                        {isPrivileged && (
                          r.has_open_job ? (
                            <button
                              data-testid={`unmount-endrun-${r.machine_code}`}
                              onClick={() => setModal({ row: r, kind: "endrun" })}
                              className="text-[11px] font-extrabold text-white bg-[#DC2626] hover:bg-red-700 px-3 py-1.5 rounded inline-flex items-center gap-1"
                              title="Ends production run AND removes the tool"
                            >
                              <StopCircle size={11} /> End Run & Unmount
                            </button>
                          ) : (
                            <button
                              data-testid={`unmount-${r.machine_code}`}
                              onClick={() => setModal({ row: r, kind: "unmount" })}
                              className="text-[11px] font-extrabold text-white bg-[#DC2626] hover:bg-red-700 px-3 py-1.5 rounded inline-flex items-center gap-1"
                            >
                              <Wrench size={11} /> Unmount
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          <AlertTriangle size={11} className="inline mr-1" />
          For tools in production, "End Run & Unmount" ends the production run (records strokes / NG / loss)
          AND removes the tool from the machine in one step.
        </p>
      </div>

      {modal?.kind === "unmount" && (
        <UnmountModal
          row={modal.row}
          onClose={() => setModal(null)}
          onDone={onUnmounted}
        />
      )}
      {modal?.kind === "endrun" && (
        <EndRunUnmountModal
          row={modal.row}
          onClose={() => setModal(null)}
          onDone={onUnmounted}
        />
      )}
    </div>
  );
};

// ---------------- Modals ----------------

const UnmountModal = ({ row, onClose, onDone }) => {
  const [reason, setReason] = useState("production_end");
  const [remark, setRemark] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/tool-mount/${row.machine_code}/unmount`, { reason, remark, effective_date: effectiveDate });
      onDone(`T${row.tool_no} removed from ${row.machine_code}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`Unmount T${row.tool_no}`} subtitle={`from ${row.machine_code}`} onClose={onClose}
      submitLabel={submitting ? "Removing…" : "Confirm Removal"} onSubmit={submit} disabled={submitting}
      testid="loaded-tools-unmount-modal">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{err}</div>}
      <label className="block text-xs font-bold text-slate-700">
        Reason
        <select data-testid="loaded-tools-unmount-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2">
          <option value="production_end">Production End</option>
          <option value="preventive_maintenance">Preventive Maintenance</option>
          <option value="breakdown">Breakdown</option>
        </select>
      </label>
      <label className="block text-xs font-bold text-slate-700">
        Effective Date <span className="text-[10px] font-normal text-slate-400">(when tool was physically removed)</span>
        <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
          data-testid="loaded-tools-unmount-date"
          className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" />
      </label>
      <label className="block text-xs font-bold text-slate-700">
        Remark
        <textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)}
          data-testid="loaded-tools-unmount-remark"
          className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
          placeholder="Describe cause / action taken" />
      </label>
    </ModalShell>
  );
};

const EndRunUnmountModal = ({ row, onClose, onDone }) => {
  const [strokes, setStrokes] = useState("");
  const [ng, setNg] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("supervisor_end");
  const [removalReason, setRemovalReason] = useState("production_end");
  const [removalRemark, setRemovalRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [jobLive, setJobLive] = useState(null);

  useEffect(() => {
    if (!row.open_job?.job_id) return;
    api.get(`/production/job/${row.open_job.job_id}`).then((r) => {
      const j = r.data?.job;
      if (j) {
        setJobLive(j);
        setStrokes(String(j.live_strokes || 0));
      }
    }).catch(() => {});
  }, [row.open_job?.job_id]);

  const submit = async () => {
    if (strokes === "" || parseInt(strokes) < 0 || parseInt(ng) < 0) {
      setErr("Strokes and NG must be non-negative numbers.");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      await api.post(`/production/${row.open_job.job_id}/end-run`, {
        strokes: parseInt(strokes) || 0,
        ng_qty: parseInt(ng) || 0,
        loss_remarks: remarks,
        end_reason: reason,
        tool_removed: true,
        removal_reason: removalReason,
        removal_remark: removalRemark,
      });
      onDone(`Ended job ${row.open_job.job_id} and unmounted T${row.tool_no}`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title={`End Run & Unmount T${row.tool_no}`} subtitle={`Job ${row.open_job?.job_id} on ${row.machine_code}`}
      onClose={onClose} submitLabel={submitting ? "Working…" : "End & Unmount"}
      onSubmit={submit} disabled={submitting}
      testid="loaded-tools-endrun-modal">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">{err}</div>}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600">
        <p><b>Tool:</b> T{row.tool_no} — {jobLive?.part_name || ""}</p>
        <p><b>Operator:</b> {jobLive?.operator_name || "—"}</p>
        <p><b>Live strokes:</b> {jobLive?.live_strokes || 0}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs font-bold text-slate-700">
          Final Strokes
          <input type="number" value={strokes} onChange={(e) => setStrokes(e.target.value)}
            data-testid="loaded-tools-endrun-strokes"
            className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" min={0} />
        </label>
        <label className="block text-xs font-bold text-slate-700">
          NG Qty
          <input type="number" value={ng} onChange={(e) => setNg(e.target.value)}
            data-testid="loaded-tools-endrun-ng"
            className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2" min={0} />
        </label>
      </div>
      <label className="block text-xs font-bold text-slate-700">
        End Reason
        <select value={reason} onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2">
          <option value="supervisor_end">Supervisor Ended Run</option>
          <option value="shift_end">Shift End</option>
          <option value="tool_change">Tool Change Required</option>
          <option value="maintenance">Maintenance Required</option>
          <option value="quality_issue">Quality Issue</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label className="block text-xs font-bold text-slate-700">
        Loss Remarks (optional)
        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
          className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
          placeholder="Losses / notes for the run" />
      </label>
      <div className="border-t border-slate-100 pt-3">
        <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-2">Tool Removal Reason</p>
        <label className="block text-xs font-bold text-slate-700">
          Reason
          <select value={removalReason} onChange={(e) => setRemovalReason(e.target.value)}
            data-testid="loaded-tools-endrun-reason"
            className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2">
            <option value="production_end">Production End</option>
            <option value="preventive_maintenance">Preventive Maintenance</option>
            <option value="breakdown">Breakdown</option>
          </select>
        </label>
        <label className="block text-xs font-bold text-slate-700 mt-2">
          Remark
          <input value={removalRemark} onChange={(e) => setRemovalRemark(e.target.value)}
            data-testid="loaded-tools-endrun-removal-remark"
            className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            placeholder="Cause / action taken" />
        </label>
      </div>
    </ModalShell>
  );
};

const ModalShell = ({ title, subtitle, onClose, onSubmit, submitLabel, disabled, children, testid }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#1A3560] text-white px-5 py-4 rounded-t-2xl">
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">{subtitle}</p>
        <p className="font-extrabold text-lg">{title}</p>
      </div>
      <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">{children}</div>
      <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
        <button onClick={onClose} className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button onClick={onSubmit} disabled={disabled}
          data-testid={`${testid}-submit`}
          className="text-sm font-extrabold text-white bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 px-4 py-2 rounded-lg">
          {submitLabel}
        </button>
      </div>
    </div>
  </div>
);

export default LoadedTools;
